import {
  DatePeriodInput,
  ErrorHint,
  FloatingBackButton,
  LazyCoverImage,
  PageContainer,
  PageCover,
  RoundedButton,
  Spinner,
  TextField,
} from "@greeter/matter";
import {
  AlertButton,
  IonContent,
  IonPage,
  useIonAlert,
  useIonRouter,
  useIonToast,
} from "@ionic/react";
import DangerText from "@greeter-venue/components/DangerText";
import DescriptiveText from "@greeter-venue/components/DescriptiveText";
import { GreeterEventCard } from "@greeter-venue/components/GreeterEventCard";
import { HighlightSpan } from "@greeter-venue/components/HighlightSpan";
import HorizontalLine from "@greeter-venue/components/HorizontalLine";
import Input from "@greeter-venue/components/Input";
import { PageTitle } from "@greeter-venue/components/PageTitle/PageTitle";
import { TitledInput } from "@greeter-venue/components/TitledInput";
import { UploadInput } from "@greeter-venue/components/UploadInput";
import React, { useCallback, useEffect, useRef } from "react";

import css from "./EditEventPage.module.scss";
import {
  EventChange,
  EventEditorApiRequirements,
  EventEditorMachine,
} from "./EventEditorMachine";
import { useActor, useSelector } from "@xstate/react";
import { adapt } from "@greeter/xstate-util";
import { classNames } from "@greeter/util";
import { hasErrors } from "@greeter/json";
import { unwrapErrorCode } from "@greeter/api";
import { useFirstInteraction } from "@greeter/hooks";

function openSupportChatHandler(errorCode: string) {
  return () => {
    const msg = `Hej! Jeg har problemer med events med fejlkode: ${errorCode}`;
    window.open(
      `https://m.me/Greeternightlife?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };
}

function buttons(args: {
  error: Error;
  onTryAgain: () => void;
}): Array<AlertButton> {
  return [
    {
      text: "Prøv igen",
      handler: args.onTryAgain,
    },
    {
      text: "Skriv til support",
      handler: openSupportChatHandler(unwrapErrorCode(args.error)),
    },
    { text: "Ignorer" },
  ];
}

export type EditEventPageProps = React.PropsWithChildren & {
  greeterEventId?: string;
  duplicating?: boolean;
  api: EventEditorApiRequirements;
};

export function EditEventPageV2({
  greeterEventId,
  duplicating,
  api,
}: EditEventPageProps) {
  const [snap, send, m] = useActor(
    EventEditorMachine.provide({
      actors: {
        createGreeterEvent: adapt(api.createGreeterEvent.bind(api)),
        updateGreeterEvent: adapt(api.updateGreeterEvent.bind(api)),
        deleteGreeterEvent: adapt(api.deleteGreeterEvent.bind(api)),
        fetchGreeterEvent: adapt(api.fetchGreeterEvent.bind(api)),
        uploadAsset: adapt(api.uploadAsset.bind(api)),
      },
    }),
    { input: { greeterEventId } }
  );
  const change = useCallback(
    function change(change: EventChange) {
      send({ type: "change", data: change });
    },
    [send]
  );

  useEffect(() => {
    return m.on("*", console.debug).unsubscribe;
  }, [m]);

  const contentRef = useRef<HTMLIonContentElement>(null);
  const state = useSelector(m, (s) => s.context.state.value);
  const period = useSelector(m, (s) => ({
    from: s.context.state.value.startsAt,
    to: s.context.state.value.endsAt,
  }));
  const isUpdating = !!greeterEventId && !duplicating;
  const validationErrors = useSelector(m, (s) => s.context.validationErrors);

  const onDatePeriodChange = useCallback(
    (d: { from: Date; to: Date }) => {
      change({ type: "setStartsAt", data: d.from });
      change({ type: "setEndsAt", data: d.to });
    },
    [change]
  );

  const router = useIonRouter();

  useEffect(
    () => m.on("created", () => router.goBack()).unsubscribe,
    [m, router]
  );
  useEffect(
    () => m.on("updated", () => router.goBack()).unsubscribe,
    [m, router]
  );
  useEffect(
    () => m.on("deleted", () => router.goBack()).unsubscribe,
    [m, router]
  );

  const [alert] = useIonAlert();
  useEffect(
    () =>
      m.on("failedToDelete", (e) =>
        alert({
          message: "Kunne ikke slette eventet",
          buttons: buttons({
            error: e.error,
            onTryAgain() {
              send({ type: "create" });
            },
          }),
        })
      ).unsubscribe,
    [m]
  );
  useEffect(
    () =>
      m.on("failedToUpdate", (e) =>
        alert({
          mode: "ios",
          message: "Kunne ikke opdatere eventet",
          buttons: buttons({
            error: e.error,
            onTryAgain() {
              send({ type: "create" });
            },
          }),
        })
      ).unsubscribe,
    [m]
  );

  useEffect(
    () =>
      m.on("failedToCreate", (e) =>
        alert({
          mode: "ios",
          message: "Kunne ikke oprettet eventet",
          buttons: buttons({
            error: e.error,
            onTryAgain() {
              send({ type: "create" });
            },
          }),
        })
      ).unsubscribe,
    [m]
  );

  const firstInteraction = useFirstInteraction();

  return (
    <IonPage>
      <IonContent ref={contentRef}>
        <PageCover style={{ zIndex: -1 }}>
          <LazyCoverImage fade src={state.coverAsset?.uri ?? state.coverUrl} />
        </PageCover>
        <PageContainer>
          <FloatingBackButton />
          <div className={css.EditEventPage}>
            <PageTitle>
              {isUpdating ? "Redigér" : "Opret"}
              <HighlightSpan> Event</HighlightSpan>
            </PageTitle>
            <TitledInput required title="Event titel">
              <Input
                id="input-title"
                detectChanges
                initialValue={state.name}
                value={state.name}
                {...classNames(
                  firstInteraction.has("input-title") &&
                    validationErrors?.fieldErrors.name &&
                    "error-outline"
                )}
                onInput={(ev) =>
                  change({
                    type: "setName",
                    data: ev.currentTarget?.value ?? "",
                  })
                }
                onBlur={() => firstInteraction.add("input-title")}
              />
              {firstInteraction.has("input-title") &&
                validationErrors?.fieldErrors.name && (
                  <ErrorHint>{validationErrors?.fieldErrors.name}</ErrorHint>
                )}
            </TitledInput>

            <TitledInput required title="Billede">
              <UploadInput
                id="input-upload"
                value={state.coverAsset?.name}
                accept="image/*"
                {...classNames(
                  firstInteraction.has("input-upload") &&
                    validationErrors?.fieldErrors.coverAsset &&
                    "error-outline"
                )}
                onFileChosen={(f) => {
                  if (f) {
                    console.debug("[onFileChosen]", { f });
                    send({ type: "uploadAsset", data: f });
                  }
                }}
                onBlur={() => firstInteraction.add("input-upload")}
              />
              {firstInteraction.has("input-upload") &&
                validationErrors?.fieldErrors.coverAsset && (
                  <ErrorHint>
                    {validationErrors?.fieldErrors.coverAsset}
                  </ErrorHint>
                )}
            </TitledInput>

            <TitledInput title="Dresscode">
              <Input
                id="input-dresscode"
                detectChanges
                defaultValue="Ingen"
                initialValue={state.dressCode}
                onInput={(ev) =>
                  change({ type: "setDressCode", data: ev.currentTarget.value })
                }
                onBlur={() => firstInteraction.add("input-dresscode")}
              />
              {firstInteraction.has("input-dresscode") &&
                validationErrors?.fieldErrors.dressCode && (
                  <ErrorHint>
                    {validationErrors?.fieldErrors.dressCode}
                  </ErrorHint>
                )}
            </TitledInput>
            <TitledInput required title="Start- og sluttidspunkt">
              <DatePeriodInput
                id="input-period"
                theme="dark"
                period={period}
                onChange={onDatePeriodChange}
                onBlur={() => firstInteraction.add("input-period")}
              />
              {firstInteraction.has("input-period") &&
                validationErrors?.fieldErrors.startsAt && (
                  <ErrorHint>
                    {validationErrors?.fieldErrors.startsAt}
                  </ErrorHint>
                )}
              {firstInteraction.has("input-period") &&
                validationErrors?.fieldErrors.endsAt && (
                  <ErrorHint>{validationErrors?.fieldErrors.endsAt}</ErrorHint>
                )}
            </TitledInput>
            <TitledInput required title="Beskrivelse">
              <TextField
                id="input-description"
                value={state.description}
                {...classNames(
                  firstInteraction.has("input-description") &&
                    validationErrors?.fieldErrors.description &&
                    "error-outline"
                )}
                onInput={(ev) =>
                  change({
                    type: "setDescription",
                    data: ev.currentTarget.value,
                  })
                }
                onBlur={() => firstInteraction.add("input-description")}
              />
              {firstInteraction.has("input-description") &&
                validationErrors?.fieldErrors.description && (
                  <ErrorHint>
                    {validationErrors?.fieldErrors.description}
                  </ErrorHint>
                )}
            </TitledInput>
            <TitledInput title="">
              <HorizontalLine beforeSlot={<h4>Forhåndsvisning</h4>} />
              <div
                style={{
                  display: "grid",
                  // gridTemplateColumns: "1fr 3fr",
                  placeItems: "center",
                }}
              >
                <GreeterEventCard
                  name={state.name}
                  description={state.description}
                  endsAt={state.endsAt}
                  coverUrl={state.coverAsset.uri}
                />
              </div>
              {!isUpdating && (
                <>
                  <RoundedButton
                    active
                    disabled={hasErrors(validationErrors)}
                    onClick={() => send({ type: "create" })}
                    activeClassName={css.ActionButton}
                  >
                    Opret event
                  </RoundedButton>
                  <DescriptiveText>
                    Har du rettelser, kan du altid <br />
                    gå tilbage og redigere eventet
                  </DescriptiveText>
                </>
              )}
              {isUpdating && (
                <>
                  <RoundedButton
                    active
                    disabled={hasErrors(validationErrors)}
                    onClick={() => send({ type: "update" })}
                    activeClassName={css.ActionButton}
                  >
                    Opdater event
                  </RoundedButton>
                  <div
                    style={{
                      width: "100%",
                      textAlign: "center",
                      margin: "1rem 0",
                    }}
                    onClick={() => {
                      alert({
                        mode: "ios",
                        header: "Du er igang med at slette eventet",
                        message:
                          "Denne handling vil slette eventet. Det er ikke muligt at gendanne eventet efter denne handling er udført",
                        buttons: [
                          {
                            text: "Slet event",
                            handler() {
                              send({ type: "delete" });
                            },
                          },
                          { text: "Annuller" },
                        ],
                      });
                    }}
                  >
                    <DangerText>Slet dette event</DangerText>
                  </div>
                </>
              )}
            </TitledInput>
          </div>
        </PageContainer>
        <Spinner
          reason={
            snap.matches({ bg: { event: "creating" } })
              ? "Opretter"
              : snap.matches({ bg: { event: "updating" } })
              ? "Gemmer"
              : snap.matches({ bg: { event: "deleting" } })
              ? "Sletter"
              : snap.matches({ bg: { event: "fetching" } }) && "Gør klar"
          }
        />
      </IonContent>
    </IonPage>
  );
}
